import React, { FC, useEffect, useRef, useState } from 'react';
import styles from './alert.module.scss';
import { useTranslation } from 'react-i18next';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { IonContent, IonModal } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import { ALERT } from '@flexo/reducers';
import { useLocation } from 'react-router-dom';
import { useOnClickOutside } from '@flexo/hooks';
import AlertWrongSize from './alert-components/alert-wrong-size';

import AlertConfirmCredentialsChanges from './alert-components/alert-confirm-credentials-changes';


import AlertConfirmCredentialsMobile from './alert-components/alert-confirm-credentials-mobile';

import UnifiedBanner from './alert-components/unified-banner';

// Removed AlertContext - we'll pass the function as props instead

export const Alert: FC<any> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const alertStore = useSelector((state: any) => state.alert);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
  const [lastTouchY, setLastTouchY] = useState<number | null>(null);
  const [lastTouchTime, setLastTouchTime] = useState<number | null>(null);
  const [translateY, setTranslateY] = useState(0);
  const [isBeingDragged, setIsBeingDragged] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);

  const [insets, setInsets] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  const modal = useRef<HTMLIonModalElement>(null);

  const ENV = (import.meta as any).env;

  // Function to animate the hiding of the alert
  const animatedHide = () => {
    if (isBanner()) {
      setIsDismissing(true);
      // Let the animation complete before dispatching any actions
      // CSS animation duration is 0.3s (300ms), so we wait for that exact duration
      setTimeout(() => {
        // After animation completes, remove the component
        dispatch(ALERT.ResetAlert());
      }, 300);
    } else {
      // For non-banner alerts, use the standard behavior
      hideAlert();
    }
  };

  const hideAlert = (immediate = false) => {
    if (isBanner()) {
      setIsDismissing(true);
      // Let the animation complete before dispatching any actions
      // CSS animation duration is 0.3s (300ms), so we wait for that exact duration
      setTimeout(() => {
        // After animation completes, remove the component
        dispatch(ALERT.ResetAlert());
      }, immediate ? 0 : 300);
    } else {
      dispatch(ALERT.HideAlert());
      if (immediate) {
        dispatch(ALERT.ResetAlert());
      } else {
        setTimeout(() => {
          dispatch(ALERT.ResetAlert());
        }, 1100);
      }
    }
  };

  useEffect(() => {
    const fetchInsets = async () => {
      try {
        const { insets } = await SafeArea.getSafeAreaInsets();
        setInsets(insets);
      } catch (error) {
        // console.error('Error fetching safe area insets:', error);
      }
    };

    fetchInsets();
  }, []);

  // Reset banner state when a new alert appears
  useEffect(() => {
    if (alertStore.alert.visible) {
      setTranslateY(0);
      setIsDismissing(false);
      setIsBeingDragged(false);
      setTouchStartY(null);
      setTouchStartTime(null);
      setLastTouchY(null);
      setLastTouchTime(null);
    }
  }, [alertStore.alert.visible]);

  // Use the outside click hook to dismiss banners when clicking outside
  useOnClickOutside(bannerRef, () => {
    if (isBanner() && alertStore.alert.visible && !isDismissing) {
      animatedHide();
    }
  });

  // Handle touch events for swipe-to-dismiss with interactive follow (iOS-like behavior)
  const handleTouchStart = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const currentTime = Date.now();
    
    setTouchStartY(currentY);
    setTouchStartTime(currentTime);
    setLastTouchY(currentY);
    setLastTouchTime(currentTime);
    setIsBeingDragged(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY === null) return;
  
    const currentY = e.touches[0].clientY;
    const currentTime = Date.now();
    const diff = touchStartY - currentY;
  
    // Update tracking for velocity calculation
    setLastTouchY(currentY);
    setLastTouchTime(currentTime);
  
    if (diff > 0) {
      // Upward drag - direct follow with slight easing
      setTranslateY(-diff);
    } else if (diff < 0 && Math.abs(diff) < 20) {
      // Small downward drag - add resistance (iOS-like rubber band)
      setTranslateY(-diff * 0.3);
    }
  };
  
  const handleTouchEnd = () => {
    if (touchStartY === null || lastTouchY === null || touchStartTime === null || lastTouchTime === null) {
      setIsBeingDragged(false);
      setTranslateY(0);
      return;
    }
  
    const totalDistance = touchStartY - lastTouchY;
    const totalTime = lastTouchTime - touchStartTime;
    const velocity = totalTime > 0 ? totalDistance / totalTime : 0;
  
    setIsBeingDragged(false);
  
    // iOS-like dismissal logic: consider both distance and velocity
    const distanceThreshold = 60; // pixels
    const velocityThreshold = 0.3; // pixels per ms (equivalent to ~300px/s)
  
    const shouldDismiss = 
      translateY < -distanceThreshold || // Dragged far enough
      (velocity > velocityThreshold && translateY < -20); // Fast upward flick
  
    if (shouldDismiss) {
      animatedHide();
    } else {
      setTranslateY(0);
    }
  
    // Reset tracking
    setTouchStartY(null);
    setTouchStartTime(null);
    setLastTouchY(null);
    setLastTouchTime(null);
  };
  



  // Monitor URL changes and dismiss banners when URL changes
  useEffect(() => {
    const currentPath = location.pathname;
    // Only dismiss if the path has actually changed
    if (alertStore.alert.visible && isBanner() && previousPathRef.current !== currentPath) {
      animatedHide();
    }
    // Update the ref with current path
    previousPathRef.current = currentPath;
  }, [location.pathname, alertStore.alert.visible]);

  function getAlertContent() {
    // No longer need the context provider
    return getAlertComponent();
  }

  function getAlertComponent() {
    let content = <></>;

    switch (alertStore.alert.type) {
      // Error banners using unified component
      case 'BANNER_UNEXPECTED_ERROR':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.unexpected_error" 
          id="banner-unexpected-error" 
        />;
        break;
      case 'BANNER_NOT_ALLOWED':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.not_allowed" 
          id="banner-not-allowed" 
        />;
        break;
      case 'BANNER_WRONG_REQUEST_DATA':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.wrong_request_data" 
          id="banner-wrong-request-data" 
        />;
        break;
      case 'BANNER_SESSION_EXPIRED':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.session_expired" 
          id="banner-session-expired" 
        />;
        break;
      case 'BANNER_WRONG_LOGIN':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.wrong_login" 
          id="banner-wrong-login"
          navigationAction={{
            text: t('alerts.wrong_login.link', { returnObjects: false }) as string,
            path: '/forgot-password'
          }}
        />;
        break;
      case 'BANNER_INVALID_CREDENTIALS':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.invalid_credentials" 
          id="banner-invalid-credentials" 
        />;
        break;
      case 'BANNER_WRONG_PARAMS':
        content = <UnifiedBanner 
          type="error" 
          messageKey="alerts.wrong_params" 
          id="banner-wrong-params" 
        />;
        break;

      // Success banners using unified component
      case 'BANNER_PASSWORD_RESET_SUCCESS':
        content = <UnifiedBanner 
          type="success" 
          messageKey="banners.password-reset-success" 
          id="banner-password-reset-success" 
        />;
        break;
      case 'BANNER_PASSWORD_RESET_SUCCESS_MOBILE':
        content = <UnifiedBanner 
          type="success" 
          messageKey="banners.password-reset-success" 
          id="banner-password-reset-success-mobile" 
        />;
        break;
      case 'BANNER_REGISTRATION_SUCCESS':
        content = <UnifiedBanner 
          type="success" 
          messageKey="banners.registration_success" 
          id="banner-registration-success" 
        />;
        break;
      case 'BANNER_LOGOUT_SUCCESS':
        content = <UnifiedBanner 
          type="success" 
          messageKey="banners.logout_success" 
          id="banner-logout-success" 
        />;
        break;
      case 'BANNER_CHANGE_USERNAME_SUCCESS':
        content = <UnifiedBanner 
          type="success" 
          messageKey="banners.change_username_success" 
          id="banner-change-username-success" 
        />;
        break;
  

      case 'BANNER_CONFIRM_CREDENTIALS_CHANGES_SUCCESS':
        content = <UnifiedBanner 
          type="success" 
          messageKey="banners.credentials_changes_success" 
          id="banner-credentials-changes-success" 
        />;
        break;
      case 'ALERT_CONFIRM_CREDENTIALS_CHANGES':
        content = <AlertConfirmCredentialsChanges />;
        break;
      case 'ALERT_CONFIRM_CREDENTIALS_CHANGES_MOBILE':
        content = <AlertConfirmCredentialsMobile type="credentials"  />;
        break;
      case 'CONFIRM-DELETE-ACCOUNT-MOBILE':
        content = <AlertConfirmCredentialsMobile type="delete" />;
        break;

      case 'ALERT_SCREEN_SIZE':
        content = <AlertWrongSize />;
        break;

      default:
        content = <>wrong alert type</>;
        break;
    }

    return content;
  }

  // Check if the current alert is a banner (non-blocking) or an alert (modal)
  function isBanner() {
    const type = alertStore?.alert?.type as string || '';
    // If the type starts with "BANNER_", it's a banner
    return type.startsWith('BANNER_');
  }

  // this works in reverse, if the alert type is not in the list, it will be dismissable
  function checkBackdropDismissCapability() {
    return ![
      'ALERT_CONFIRM_CREDENTIALS_CHANGES',
      'ALERT_SCREEN_SIZE',
      'ALERT_CONFIRM_DELETE_ACCOUNT_MOBILE',
    ].includes((alertStore?.alert?.type as string) || '');
  }
  
  // Get the appropriate banner animation classes
  function getBannerAnimationClass() {
    if (!alertStore.alert.visible) return '';
    
    if (isDismissing) {
      return styles.BannerExiting;
    }
    
    return styles.BannerEntering;
  }

  // If it's a banner, render as a non-blocking overlay
  if (isBanner() && alertStore.alert.visible) {
    return (
      <div className={styles.BannerOverlay}>
        <div 
          className={`${styles.BannerContainer} ${getBannerAnimationClass()}`}
          ref={bannerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          // onClick={handleBannerContentClick}
          style={{ 
            transform: `translateY(${translateY}px)`,
            transition: isBeingDragged 
              ? 'none' 
              : translateY === 0 
                ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' // Spring-back animation
                : 'transform 0.3s ease-out' // Normal dismiss animation
          }}
        >
          {getAlertContent()}
          <div className={styles.BannerDragIndicator}></div>
        </div>
      </div>
    );
  }

  // Otherwise, render as a modal (existing behavior)
  return (
    <>
      {alertStore.alert.visible && !isBanner() && (
        <div className={styles['modal-overlay-backdrop']} />
      )}
      <IonModal
        isOpen={alertStore.alert.visible}
        className={
          `${ENV?.VITE_APP_IS_WEB === 'true'
            ? styles.Alert__Modal__Web
            : styles.Alert__Modal}

            ${alertStore.alert.type === 'ALERT_SCREEN_SIZE' ? styles.Alert__Screen__Size : ''}
            `
          
        }
        onDidDismiss={() => hideAlert()}
        id="alert-modal-wrapper"
        ref={modal}
        backdropDismiss={checkBackdropDismissCapability()}
        animated={false}
      >
        <IonContent>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'row',
            }}
          >
            {getAlertContent()}
          </div>
        </IonContent>
      </IonModal>
    </>
  );
};

export default Alert;

// Alert component only for alerts
// Separated component for inputs, etc.
// Instead of global provider for alerts, just confirm the data by sending it from the triggere component(parent)
